import React, { useState, useEffect } from 'react';
import {
  Compass, Footprints, Box, Rocket, Crown,
  ChevronRight, Star, Heart, Eye, Flame,
  Shield, MapPin, Zap, Target, Award,
  Phone, Image, Puzzle, Check, Lock, Unlock,
  Tag, TrendingUp, BarChart3, Search, Layout, Infinity
} from 'lucide-react';
import { api } from '../services/api';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';

const LEVEL_CONFIG = {
  1: { name: 'Chegando na Comunidade', icon: Compass,    color: '#9ca3af', bg: 'bg-gray-50',    border: 'border-gray-200',  text: 'text-gray-700' },
  2: { name: 'Em Movimento',           icon: Footprints,  color: '#3b82f6', bg: 'bg-blue-50',    border: 'border-blue-200',  text: 'text-blue-700' },
  3: { name: 'Perfil Estruturado',     icon: Box,         color: '#f59e0b', bg: 'bg-amber-50',   border: 'border-amber-200', text: 'text-amber-700' },
  4: { name: 'Negócio em Destaque',    icon: Rocket,      color: '#7c3aed', bg: 'bg-purple-50',  border: 'border-purple-200',text: 'text-purple-700' },
  5: { name: 'Referência Local',       icon: Crown,       color: '#eab308', bg: 'bg-yellow-50',  border: 'border-yellow-200',text: 'text-yellow-800' },
};

const MISSION_ICONS = {
  base: Puzzle, trust: Shield, activity: Flame, engagement: Heart,
};

const BADGE_ICONS = {
  'puzzle': Puzzle, 'shield-check': Shield, 'flame': Flame, 'star': Star,
  'eye': Eye, 'heart': Heart, 'rocket': Rocket, 'crown': Crown,
  'compass': Compass, 'map-pin': MapPin, 'image': Image, 'phone': Phone,
};

const ELIGIBILITY_ICONS = {
  'tag': Tag, 'trending-up': TrendingUp, 'puzzle': Puzzle, 'star': Star,
  'bar-chart': BarChart3, 'search': Search, 'infinity': Infinity,
  'layout': Layout, 'crown': Crown, 'award': Award,
};

const NEXT_LEVEL_BENEFITS = {
  2: 'Seu negócio começa a ganhar mais visibilidade na plataforma.',
  3: 'Habilita promoções, vagas e aparece em recomendações básicas.',
  4: 'Prioridade na busca, destaque visual e mais limites de promoções.',
  5: 'Selo premium, prioridade máxima, elegibilidade para curadoria especial.',
};

function ScoreRing({ score, size = 120 }) {
  const level = score >= 85 ? 5 : score >= 70 ? 4 : score >= 50 ? 3 : score >= 30 ? 2 : 1;
  const config = LEVEL_CONFIG[level];
  const r = (size / 2) - 8;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f3f4f6" strokeWidth="8" />
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={config.color}
          strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: config.color }}>{score}</span>
        <span className="text-[10px] text-muted-foreground">pontos</span>
      </div>
    </div>
  );
}

export default function JornadaNegocio({ businessId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [fullData, setFullData] = useState(null);

  // Load cached score first (fast)
  useEffect(() => {
    if (!businessId) return;
    const fetchScore = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/v2/gamification/business/${businessId}/score`);
        setData(res.data);
      } catch (err) {
        console.error('Erro ao carregar jornada:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScore();
  }, [businessId]);

  // Load full data only when expanded
  useEffect(() => {
    if (!expanded || !businessId || fullData) return;
    api.get(`/v2/gamification/business/${businessId}/full`)
      .then(res => { setFullData(res.data); setData(res.data); })
      .catch(() => {});
  }, [expanded, businessId, fullData]);

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-50 rounded-xl p-6 h-32" data-testid="jornada-loading" />
    );
  }

  if (!data) return null;

  // Handle both cached score format and full format
  const score = data.score || 0;
  const levelNum = typeof data.level === 'object' ? data.level?.level : (data.level || 1);
  const config = LEVEL_CONFIG[levelNum];
  const LevelIcon = config.icon;
  
  // For next level info: full data has it as object, cached has level as number
  const nextLevel = data.next_level || (levelNum < 5 ? { level: levelNum + 1, min: [0,30,50,70,85][levelNum] } : null);
  const ptsToNext = data.points_to_next || (nextLevel ? Math.max(0, nextLevel.min - score) : 0);

  const pendingMissions = (fullData?.missions || data?.missions || []).filter(m => !m.completed).slice(0, 5);
  const completedMissions = (fullData?.missions || data?.missions || []).filter(m => m.completed);
  const unlockedAchievements = (fullData?.achievements || data?.achievements || []).filter(a => a.unlocked);
  const activeBadges = (fullData?.badges || data?.badges || []).filter(b => b.active);

  const badgesCount = activeBadges.length || (data.badges_active?.length || 0);

  // Progress within current level
  const levelRanges = [{ min: 0, max: 29 }, { min: 30, max: 49 }, { min: 50, max: 69 }, { min: 70, max: 84 }, { min: 85, max: 100 }];
  const currentRange = levelRanges[levelNum - 1] || levelRanges[0];
  const progressInLevel = currentRange.max > currentRange.min ? ((score - currentRange.min) / (currentRange.max - currentRange.min)) * 100 : 100;

  return (
    <div
      className={`rounded-xl border ${config.border} ${config.bg} overflow-hidden transition-all`}
      data-testid="jornada-negocio"
    >
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 sm:p-5 flex items-center gap-4 hover:bg-white/30 transition-colors"
        data-testid="jornada-toggle"
      >
        <ScoreRing score={score} size={80} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <LevelIcon className="w-4 h-4" style={{ color: config.color }} />
            <span className={`text-sm font-bold ${config.text}`}>{config.name}</span>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              Nível {levelNum}
            </Badge>
          </div>

          {nextLevel ? (
            <>
              <p className="text-xs text-muted-foreground mb-2">
                Faltam <strong className={config.text}>{ptsToNext} pontos</strong> para {LEVEL_CONFIG[nextLevel.level]?.name}
              </p>
              <Progress value={progressInLevel} className="h-1.5" />
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Seu negócio alcançou o nível máximo na plataforma!
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {badgesCount > 0 && (
            <span className="text-xs text-muted-foreground hidden sm:block">{badgesCount} selos</span>
          )}
          <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-5 border-t border-white/50">

          {/* Loading full data */}
          {!fullData && (
            <div className="flex items-center justify-center py-4 mt-4">
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground ml-2">Carregando detalhes...</span>
            </div>
          )}

          {fullData && (<>
          {/* Missões do momento */}
          {pendingMissions.length > 0 && (
            <div data-testid="jornada-missions">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 mt-4 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5" /> Missões do momento
              </h4>
              <div className="space-y-2">
                {pendingMissions.map(m => {
                  const MIcon = MISSION_ICONS[m.category] || Target;
                  return (
                    <div key={m.id} className="flex items-center gap-3 bg-white/60 rounded-lg px-3 py-2.5" data-testid={`mission-${m.id}`}>
                      <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-sm">
                        <MIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                      <span className="text-sm flex-1">{m.label}</span>
                      <Badge className="bg-white/80 text-[10px] font-bold shadow-sm border">+{m.points}pts</Badge>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 italic">
                Cada informação adicionada aumenta suas chances de ser encontrado.
              </p>
            </div>
          )}

          {/* Conquistas */}
          {unlockedAchievements.length > 0 && (
            <div data-testid="jornada-achievements">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5" /> Conquistas desbloqueadas
              </h4>
              <div className="flex flex-wrap gap-2">
                {unlockedAchievements.map(a => {
                  const AIcon = BADGE_ICONS[a.icon] || Star;
                  return (
                    <div
                      key={a.id}
                      className="flex items-center gap-1.5 bg-white/70 rounded-full px-3 py-1.5 text-xs shadow-sm border border-white"
                      title={a.description}
                      data-testid={`achievement-${a.id}`}
                    >
                      <AIcon className="w-3.5 h-3.5" style={{ color: config.color }} />
                      <span className="font-medium">{a.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selos ativos */}
          {activeBadges.length > 0 && (
            <div data-testid="jornada-badges">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" /> Selos ativos
              </h4>
              <div className="flex flex-wrap gap-2">
                {activeBadges.map(b => {
                  const BIcon = BADGE_ICONS[b.icon] || Check;
                  return (
                    <Badge
                      key={b.id}
                      variant="outline"
                      className="gap-1 bg-white/80 border-white shadow-sm text-xs py-1"
                      data-testid={`badge-${b.id}`}
                    >
                      <BIcon className="w-3 h-3" style={{ color: config.color }} />
                      {b.label}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Funcionalidades desbloqueadas/bloqueadas */}
          {fullData.eligibility && (
            <div data-testid="jornada-eligibility">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Unlock className="w-3.5 h-3.5" /> Funcionalidades
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(fullData.eligibility).map(([key, rule]) => {
                  const EIcon = ELIGIBILITY_ICONS[rule.icon || 'star'] || Star;
                  const isEligible = rule.eligible;
                  const levelNeeded = rule.min_level;
                  const levelName = levelNeeded === 3 ? 'Perfil Estruturado' : levelNeeded === 4 ? 'Negócio em Destaque' : 'Referência Local';
                  return (
                    <div
                      key={key}
                      className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs transition-all ${
                        isEligible
                          ? 'bg-white/70 border border-green-200/50'
                          : 'bg-white/30 border border-gray-200/50 opacity-70'
                      }`}
                      data-testid={`eligibility-${key}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isEligible ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {isEligible ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`font-medium ${isEligible ? 'text-gray-800' : 'text-gray-500'}`}>
                          {rule.label}
                        </span>
                        {!isEligible && (
                          <p className="text-[10px] text-muted-foreground">
                            Requer {levelName} ({rule.min_score}+ pts)
                          </p>
                        )}
                      </div>
                      <EIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isEligible ? 'text-green-500' : 'text-gray-300'}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Movimento do negócio */}
          <div data-testid="jornada-metrics">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" /> Movimento do seu negócio
            </h4>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {[
                { label: 'Base', value: fullData.blocks?.base?.score, max: fullData.blocks?.base?.max, icon: Puzzle },
                { label: 'Qualidade', value: fullData.blocks?.quality?.score, max: fullData.blocks?.quality?.max, icon: Star },
                { label: 'Confiança', value: fullData.blocks?.trust?.score, max: fullData.blocks?.trust?.max, icon: Shield },
                { label: 'Atividade', value: fullData.blocks?.activity?.score, max: fullData.blocks?.activity?.max, icon: Flame },
                { label: 'Engajamento', value: fullData.blocks?.engagement?.score, max: fullData.blocks?.engagement?.max, icon: Heart },
              ].map(block => {
                const BIcon = block.icon;
                const pct = block.max > 0 ? (block.value / block.max) * 100 : 0;
                return (
                  <div key={block.label} className="bg-white/60 rounded-lg p-2.5 text-center">
                    <BIcon className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm font-bold">{block.value?.toFixed(1)}</p>
                    <p className="text-[9px] text-muted-foreground">de {block.max}</p>
                    <Progress value={pct} className="h-1 mt-1" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Próxima recompensa */}
          {nextLevel && (
            <div className="bg-white/50 rounded-lg p-3 border border-white" data-testid="jornada-next-reward">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                <Rocket className="w-3.5 h-3.5" /> Próxima recompensa
              </h4>
              <div className="flex items-center gap-2">
                {(() => {
                  const NextIcon = LEVEL_CONFIG[nextLevel.level]?.icon || Rocket;
                  return <NextIcon className="w-5 h-5" style={{ color: LEVEL_CONFIG[nextLevel.level]?.color }} />;
                })()}
                <div>
                  <p className="text-sm font-medium">{LEVEL_CONFIG[nextLevel.level]?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {NEXT_LEVEL_BENEFITS[nextLevel.level] || 'Novos benefícios serão desbloqueados.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Microcopy footer */}
          <p className="text-[11px] text-center text-muted-foreground italic pt-2">
            Quanto mais completo, confiável, ativo e relevante for o negócio, mais força ele ganha na comunidade.
          </p>
          </>)}
        </div>
      )}
    </div>
  );
}
